import React, { useEffect, useMemo, useState } from 'react';
import { Target, Plus, RefreshCw, Briefcase, Layers } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { ALL_FILIALES_ID } from '../data/filiales';
import {
  Avatar, Field, IdChip, ListCard, Modal, RowActions, StatCard, Table, inputCls, rowCls, tdCls,
} from '../components/ui';

const Campagnes = () => {
  const { filialeId } = useFiliale();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [activites, setActivites] = useState([]);
  const [clients, setClients] = useState([]);

  const isAllFiliales = !filialeId || filialeId === 'all' || filialeId === ALL_FILIALES_ID;

  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = '/api/campagnes';
      // Filiale spécifique : on envoie filiale_id. Sinon, consolidé (super_admin) ou filiale de l'utilisateur.
      if (!isAllFiliales) endpoint += `?filiale_id=${filialeId}`;
      const response = await http.get(endpoint);
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [activitesRes, clientsRes] = await Promise.all([http.get('/api/activites'), http.get('/api/clients')]);
      setActivites(Array.isArray(activitesRes.data) ? activitesRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch (error) {
      console.error('Erreur chargement dépendances:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDependencies();
  }, [filialeId]);

  const stats = useMemo(() => ({
    total: data.length,
    clients: new Set(data.map((i) => i.Libelle_client).filter(Boolean)).size,
    activites: new Set(data.map((i) => i.Libelle_activite).filter(Boolean)).size,
  }), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((i) =>
      [i.Libelle_campagne, i.filiale_libelle, i.Libelle_activite, i.Libelle_client].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [data, search]);

  const handleCreate = () => {
    if (isAllFiliales) {
      alert('Veuillez sélectionner une filiale spécifique dans le Périmètre avant de créer une campagne.');
      return;
    }
    setEditingItem(null);
    setFormData({ filiale_id: parseInt(filialeId, 10) });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette campagne ?')) return;
    try {
      await http.delete(`/api/campagnes/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/campagnes/${editingItem.idcampagne}`, formData);
      else await http.post('/api/campagnes', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const setId = (key) => (e) =>
    setFormData({ ...formData, [key]: e.target.value ? parseInt(e.target.value, 10) : '' });

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Campagnes"
        subtitle="Gestion des campagnes"
        icon={Target}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Target} label="Campagnes" value={stats.total} />
          <StatCard icon={Briefcase} label="Clients concernés" value={stats.clients} />
          <StatCard icon={Layers} label="Activités couvertes" value={stats.activites} />
        </div>

        <ListCard
          count={filtered.length} singular="campagne affichée" plural="campagnes affichées"
          search={search} onSearch={setSearch} placeholder="Rechercher par campagne, client, activité"
          loading={loading} hasData={data.length > 0} icon={Target}
          emptyTitle="Aucune campagne pour le moment" emptyText="Créez votre première campagne pour commencer."
          onCreate={handleCreate} createLabel="Créer une campagne" createIcon={Plus}
        >
          <Table heads={['Campagne', 'Filiale', 'Activité', 'Client', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idcampagne} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar icon={Target} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.Libelle_campagne}</p>
                      <p className="mt-0.5"><IdChip>#{item.idcampagne}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={`${tdCls} text-sm text-slate-700 dark:text-slate-300`}>{item.filiale_libelle || '-'}</td>
                <td className={`${tdCls} text-sm text-slate-700 dark:text-slate-300`}>{item.Libelle_activite || '-'}</td>
                <td className={`${tdCls} text-sm text-slate-700 dark:text-slate-300`}>{item.Libelle_client || '-'}</td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idcampagne)} />
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          title={editingItem ? 'Modifier la campagne' : 'Créer une campagne'}
          subtitle={editingItem ? editingItem.Libelle_campagne : 'Renseignez les informations de la campagne'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="space-y-5 p-6">
            <Field label="Libellé">
              <input
                type="text"
                className={inputCls}
                value={formData.Libelle_campagne || ''}
                onChange={(e) => setFormData({ ...formData, Libelle_campagne: e.target.value })}
                required
              />
            </Field>
            <Field label="Activité">
              <select className={inputCls} value={formData.idactivites || ''} onChange={setId('idactivites')} required>
                <option value="">Sélectionner...</option>
                {activites.map((a) => (
                  <option key={a.idactivites} value={a.idactivites}>{a.Libelle_activite}</option>
                ))}
              </select>
            </Field>
            <Field label="Client">
              <select className={inputCls} value={formData.idclient || ''} onChange={setId('idclient')} required>
                <option value="">Sélectionner...</option>
                {clients.map((c) => (
                  <option key={c.idclient} value={c.idclient}>{c.Libelle_client}</option>
                ))}
              </select>
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Campagnes;